import { useState, type MouseEvent } from "react";
import { Button } from "@mui/joy";
import { useStytchB2BClient } from "@stytch/react/b2b";
import axios from "../../config/axios";
import {
    AUTH_METHOD_LABELS,
    REQUESTED_ORGANIZATION_PARAM,
    directSignInMethod,
    organizationSsoConnections,
    startSignInRedirect,
} from "../../auth/authMethods";
import { listActiveOrganizations } from "../../auth/projectExchange";
import type { SessionTenancy } from "../../shared/interfaces";
import { markCompanySignInTried } from "../../utils/admissionRefusal";
import { storeAuthReturn } from "../../utils/authReturn";
import { isInIframe } from "../../utils/browserUtils";
import { tenantRoute } from "../../utils/tenantRoute";

/** Catalog pages read to find the URL's project, 100 projects each. */
const MAX_CATALOG_PAGES = 5;

/**
 * The provider organization of the project this URL names, from the person's own project catalog
 * (`/session/project-routes`, the list the project chooser shows), or null when the catalog does
 * not list it by this URL's slugs.
 */
async function urlProjectOrganization(): Promise<string | null> {
    const { companySlug, projectSlug } = tenantRoute();
    if (!companySlug || !projectSlug) return null;
    const names = (selector: string, slug: string, id: string) =>
        selector.toLowerCase() === slug.toLowerCase() || selector === id;
    let after: string | null | undefined;
    for (let page = 0; page < MAX_CATALOG_PAGES; page++) {
        const { data } = await axios.get<SessionTenancy>("/api/ai/session/project-routes", {
            params: after ? { after_project_id: after } : {},
            timeout: 5_000,
        });
        const company = data.companies.find((c) => names(companySlug, c.slug, c.company_id));
        const project = data.projects.find(
            (p) => p.company_id === company?.company_id && names(projectSlug, p.slug, p.project_id),
        );
        if (project) return project.provider_organization_id ?? null;
        after = data.projects_next_cursor;
        if (!after) return null;
    }
    return null;
}

/**
 * The sign-in action under a project URL's step-up or expired-session notice. When Stytch named
 * one OAuth or SSO method, the button starts it the way the login page's own button does, so one
 * click reaches the provider; SSO does so only when the project's organization names one
 * connection. Otherwise it opens the login picker, which enforces the same methods. A step-up's
 * picker names the project's organization, so its single sign-on starts only that
 * organization's connections; an expired session cannot read the catalog and signs in afresh.
 * Either way the person returns to this page.
 */
export default function CompanySignInButton({
    sessionExpired,
    methods,
    email,
}: {
    sessionExpired: boolean;
    methods: readonly string[] | null;
    /** The signed-in person's address: the OAuth provider's login hint. */
    email: string | undefined;
}) {
    const client = useStytchB2BClient();
    const [starting, setStarting] = useState(false);
    const { pathname, search, hash } = window.location;
    const returnPath = pathname + search + hash;
    const pickerHref = (organizationId: string | null = null) =>
        `/authenticate?company_auth=required&redirect=${encodeURIComponent(returnPath)}${
            organizationId
                ? `&${REQUESTED_ORGANIZATION_PARAM}=${encodeURIComponent(organizationId)}`
                : ""
        }`;
    // A provider's sign-in page cannot load in an iframe; the picker there signs in by popup.
    const method = sessionExpired || isInIframe() ? null : directSignInMethod(methods);
    if (!method)
        return (
            <Button
                component="a"
                loading={starting}
                onClick={(event: MouseEvent) => {
                    if (sessionExpired) return;
                    event.preventDefault();
                    markCompanySignInTried();
                    setStarting(true);
                    void urlProjectOrganization()
                        .catch(() => null)
                        .then((organizationId) =>
                            window.location.assign(pickerHref(organizationId)),
                        );
                }}
                href={pickerHref()}
            >
                {sessionExpired ? "Sign in again" : "Sign in to company"}
            </Button>
        );
    const start = async () => {
        markCompanySignInTried();
        storeAuthReturn(returnPath);
        setStarting(true);
        let organizationId: string | null = null;
        try {
            let ssoConnectionId: string | undefined;
            if (method === "sso") {
                organizationId = await urlProjectOrganization();
                const [only, ...others] = organizationId
                    ? organizationSsoConnections(
                          await listActiveOrganizations(client),
                          organizationId,
                      )
                    : [];
                if (!only || others.length > 0)
                    throw new Error("This project's organization names no one SSO connection.");
                ssoConnectionId = only.connection_id;
            }
            await startSignInRedirect(client, method, {
                redirectUrl: `${window.location.origin}/authenticate`,
                email,
                ssoConnectionId,
            });
        } catch {
            // The picker still offers every method the organization accepts, and a choice
            // among its connections.
            window.location.assign(pickerHref(organizationId));
        }
    };
    return (
        <Button loading={starting} onClick={() => void start()}>
            Sign in with {AUTH_METHOD_LABELS[method]}
        </Button>
    );
}
